import type {
  InterfacePageEntity,
  InterfacePageEntityComponentType,
} from '@unifyapps/defs/types/page';
import { createEventBus } from '../../emitter';

/**
 * All I/O events that travel on the walkthrough channel.
 * These are deliberately separated from InterfaceEventBusType so that:
 *   1. Walkthrough-only modules never accidentally couple to app-level events.
 *   2. Generic hosts (non-no-code webpages) can mount the walkthrough runtime
 *      without importing any no-code/interface types beyond this file.
 *
 * The underlying ChannelEventBus instance is shared with the app bus when both
 * use the same instanceId — the type split is purely for type-safety, not for
 * creating a separate physical channel.
 */
export type WalkthroughInstanceEventBusType = {
  // Triggered when a view (page/route) is loaded in the host
  onPageLoad?: {
    pageId: string;
    componentType: InterfacePageEntityComponentType;
    /**
     * The page instance this event targets. Omit (or leave undefined) for the
     * main page (top-level InterfacePage with no instance ancestor).
     */
    instanceId?: string;
  };

  // Triggered when a view (page/route) is unloaded in the host
  onPageUnload?: {
    pageId: string;
    componentType: InterfacePageEntityComponentType;
    /**
     * The page instance this event targets. Omit (or leave undefined) for the
     * main page (top-level InterfacePage with no instance ancestor).
     */
    instanceId?: string;
  };

  // Triggered when walkthroughs are fetched and set for a specific view
  onSetFetchedWalkthroughs?: {
    pageId: string;
    walkthrough: InterfacePageEntity | undefined;
  };

  // Triggered to notify components that walkthrough state has changed
  // Used by useWalkthroughChangeNotification to trigger re-rendering
  notifyWalkthroughChange?: { pageId: string };

  // Triggered to control the current step in a walkthrough
  onControlStep?: { method: 'next' | 'previous' };

  // Triggered to go to a specific step in a walkthrough
  onGoToStep?: { stepIndex: number };

  // Triggered to start or stop a walkthrough
  onControlWalkthrough?: {
    method: 'start' | 'stop';
    walkthroughId?: string;
    initialStepIndex?: number;
  };

  // Walkthrough lifecycle hooks — allow target pages to run setup/teardown
  // before/after a walkthrough starts/ends.

  // Sent by walkthrough to target page before the walkthrough activates
  onWalkthroughPreStart?: { walkthroughId: string };
  // Target page responds with whether it is handling the pre-start hook
  onWalkthroughPreStartAck?: { handling: boolean };
  // Target page signals that its pre-start processing is complete
  onWalkthroughPreStartDone?: Record<string, never>;

  // Sent by walkthrough to target page just before the walkthrough ends
  onWalkthroughPreEnd?: { walkthroughId: string };
  // Target page responds with whether it is handling the pre-end hook
  onWalkthroughPreEndAck?: { handling: boolean };
  // Target page signals that its pre-end processing is complete
  onWalkthroughPreEndDone?: Record<string, never>;
};

/**
 * Creates (or retrieves from the global registry) the walkthrough-scoped event bus
 * for a given instanceId.
 *
 * Uses a `ws:` prefixed channelId to isolate walkthrough events from app-level
 * events that share the same underlying ChannelEventBus registry. This prevents
 * event name collisions between InterfaceEventBusType and WalkthroughInstanceEventBusType.
 *
 * Generic hosts: pass a stable identifier for the walkthrough instance (e.g. a
 * normalized pathname, route-template key, or any opaque string agreed on with
 * PM). No-code: pass the interface record id, same as today.
 */
export function createWalkthroughInstanceEventBus(instanceId: string) {
  return createEventBus<WalkthroughInstanceEventBusType>(`ws:${instanceId}`);
}
